import type { UIMessage } from 'ai';
import { v4 } from 'uuid';
import { z } from 'zod';
import { coco, ResourceType } from '@cotera/api';
import { ToolKey } from '../tool-key';
import type { CocoToolKeys } from './types';
import type { JsonValue } from '../serializable';
import { EditorState, type DocumentState } from '../dg/document/editor';
import { collectWorkspaceResourceRefs } from '../dg/document/collect-workspace-resource-refs';
import { BuildExplorer } from '../dg/commit/build-explorer';
import {
  emptyWorkspaceRenderBundle,
  noopWorkspaceDomainLoader,
  type WorkspaceDomainLoader,
} from './workspace-prefetch';
import { WorkspacePromptEditor } from './workspace-prompt-editor';
import { ChatAction } from './chat-action';
import {
  ToolPart,
  hydratePart,
  type StoredSystemMessageUIPart,
} from './message-parts';
import type { MessagePart } from './message-parts';
import {
  COCO_ASK_FOR_CREDENTIALS_TOOL_KEY,
  COCO_LOAD_RESOURCE_TOOL_KEY,
  agentIdToAgentToolKey,
  workflowIdToWorkflowToolKey,
} from './coco-tools';
import type { Agent } from './agent';
import type {
  CoteraAgentMessage,
  CoteraPart,
} from '../llm/cotera-agent-message';
import type { AssistantMessageLlmUsage } from './assistant-message-llm-usage';

export {
  ToolPart,
  WorkflowToolCallPart,
  createToolPart,
} from './message-parts';
export type { WorkflowToolCallPhase } from './message-parts';

export type MessageType = 'chat' | 'compaction' | 'hidden';

/** Stable id for the persisted conversation system row in `chat_messages`. */
export const CONVERSATION_SYSTEM_MESSAGE_ID = 'conversation-system';

export const STORED_SYSTEM_MESSAGE_PART_TYPE = 'stored-system-message' as const;

export const storedSystemMessagePayloadSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('coco') }),
  z.object({ t: z.literal('ad-hoc'), message: z.string() }),
]);

export type StoredSystemMessagePayload = z.infer<
  typeof storedSystemMessagePayloadSchema
>;

export function parseStoredSystemMessagePayload(
  data: unknown
): StoredSystemMessagePayload | null {
  const parsed = storedSystemMessagePayloadSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
}

export function storedSystemMessagePart(
  payload: StoredSystemMessagePayload
): StoredSystemMessageUIPart {
  return { type: STORED_SYSTEM_MESSAGE_PART_TYPE, payload };
}

function storedSystemMessageUiPart(
  payload: StoredSystemMessagePayload
): NonNullable<UIMessage['parts']>[number] {
  return storedSystemMessagePart(payload) as unknown as NonNullable<
    UIMessage['parts']
  >[number];
}

function extractStoredPayloadFromParts(
  parts: unknown[]
): StoredSystemMessagePayload | null {
  for (const part of parts) {
    if (typeof part !== 'object' || part === null || !('type' in part)) {
      continue;
    }
    const partType = (part as { type: unknown }).type;
    if (partType === STORED_SYSTEM_MESSAGE_PART_TYPE && 'payload' in part) {
      return parseStoredSystemMessagePayload(
        (part as { payload: unknown }).payload
      );
    }
  }
  return null;
}

/** Read coco/ad-hoc payload from a `chat_messages.message` column value. */
export function parseStoredSystemMessageBody(
  messageBody: unknown
): StoredSystemMessagePayload | null {
  if (
    typeof messageBody !== 'object' ||
    messageBody === null ||
    !('parts' in messageBody) ||
    !Array.isArray((messageBody as { parts: unknown }).parts)
  ) {
    return null;
  }
  return extractStoredPayloadFromParts(
    (messageBody as { parts: unknown[] }).parts
  );
}

/** Shape written to `chat_messages.message` for conversation system rows. */
export function persistedSystemMessageBody(
  payload: StoredSystemMessagePayload
): { parts: StoredSystemMessageUIPart[] } {
  return { parts: [storedSystemMessagePart(payload)] };
}

/** Whether a {@link UIMessage} contains a compact persisted system part. */
export function uiMessageHasStoredSystemPart(uiMessage: UIMessage): boolean {
  return (uiMessage.parts ?? []).some((part) => {
    const partType = (part as { type?: string }).type;
    return partType === STORED_SYSTEM_MESSAGE_PART_TYPE;
  });
}

export function storedPayloadToSystemText(
  payload: StoredSystemMessagePayload
): string {
  if (payload.t === 'coco') {
    return coco.SYSTEM_MESSAGE;
  }
  return payload.message;
}

/**
 * Strip or expand Cotera-only parts (`stored-system-message`, `editor-state`) so
 * messages pass AI SDK {@link validateUIMessages} / `useChat` schemas.
 */
export function toAiSdkCompatibleUIMessages(
  messages: UIMessage[]
): UIMessage[] {
  const result: UIMessage[] = [];
  for (const msg of messages) {
    if (msg.id === CONVERSATION_SYSTEM_MESSAGE_ID) {
      continue;
    }
    const parts = msg.parts ?? [];
    const nextParts: UIMessage['parts'] = [];
    for (const part of parts) {
      const partAny = part as { type?: string; payload?: unknown };
      const partType = partAny.type;
      if (partType === STORED_SYSTEM_MESSAGE_PART_TYPE) {
        const payload = parseStoredSystemMessagePayload(partAny.payload);
        if (payload !== null) {
          nextParts.push({
            type: 'text',
            text: storedPayloadToSystemText(payload),
          } as UIMessage['parts'][number]);
        }
        continue;
      }
      if (partType === 'editor-state') {
        continue;
      }
      nextParts.push(part);
    }
    if (nextParts.length === 0 && msg.role === 'system') {
      continue;
    }
    result.push({ ...msg, parts: nextParts });
  }
  return result;
}

/** Rehydrate a `chat_messages.message` column value into a domain {@link UIMessage}. */
export function uiMessageFromPersistedSystemBody(
  messageId: string | null,
  messageBody: unknown
): UIMessage | null {
  if (messageId !== CONVERSATION_SYSTEM_MESSAGE_ID) {
    return null;
  }
  const payload = parseStoredSystemMessageBody(messageBody);
  if (payload === null) {
    return null;
  }
  return {
    id: CONVERSATION_SYSTEM_MESSAGE_ID,
    role: 'system',
    parts: [storedSystemMessageUiPart(payload)],
  };
}

/**
 * Type guard to check if a MessagePart is a credential request tool part.
 * Returns true for ask-for-credentials tool parts and load-resource (or legacy load-tool) parts with credentialInfo output.
 */
function isCredentialRequestToolPart(p: MessagePart): p is ToolPart {
  if (!p.isToolPart()) {
    return false;
  }

  const isAskForCredentials = p
    .toolKey()
    .defensiveEqual(COCO_ASK_FOR_CREDENTIALS_TOOL_KEY);
  if (isAskForCredentials) {
    return true;
  }

  const tk = p.toolKey().toString();
  const isLoadResourcePart =
    tk === COCO_LOAD_RESOURCE_TOOL_KEY || tk === 'coco/load-tool';
  if (!isLoadResourcePart) {
    return false;
  }

  const output = p.getOutput<{
    credentialInfo?: {
      provider: string;
      strategies: { name: string }[];
    };
  }>();
  return output?.credentialInfo !== undefined;
}

/**
 * Media types that the AI SDK providers support natively as file parts.
 * Everything else needs to be converted to inline text (when it is
 * text-like) or replaced with a short placeholder (when it is binary).
 */
const SUPPORTED_FILE_MEDIA_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
]);

/**
 * Non-`text/*` media types whose bodies are still text and safe to inline.
 * Used alongside a `text/*` prefix check and `+json`/`+xml` suffix check.
 */
const TEXT_LIKE_APPLICATION_MEDIA_TYPES = new Set([
  'application/json',
  'application/ld+json',
  'application/xml',
  'application/xhtml+xml',
  'application/javascript',
  'application/ecmascript',
  'application/typescript',
  'application/csv',
  'application/x-ndjson',
  'application/yaml',
  'application/x-yaml',
  'application/x-sh',
  'application/graphql',
  'application/sql',
]);

/**
 * Upper bound on the number of characters we will inline for a single
 * uploaded file. The provider rejects any single text content block longer
 * than 10 MiB (10_485_760 chars); we cap well below that to leave headroom
 * for the code-fence wrapper and the rest of the conversation.
 */
const MAX_INLINED_FILE_CHARS = 1_000_000;

/**
 * Whether a file's decoded body can be safely inlined as text. Binary media
 * (video, audio, archives, `application/octet-stream`, …) return false: their
 * raw bytes decode to garbage and can blow past the provider's per-block size
 * limit, so we replace them with a placeholder instead of inlining them.
 */
function isTextLikeMediaType(mediaType: string): boolean {
  const normalized = mediaType.split(';')[0]!.trim().toLowerCase();
  if (normalized.startsWith('text/')) {
    return true;
  }
  if (normalized.endsWith('+json') || normalized.endsWith('+xml')) {
    return true;
  }
  return TEXT_LIKE_APPLICATION_MEDIA_TYPES.has(normalized);
}

/**
 * Extract text content from a data URL (base64 or percent-encoded).
 * Returns null if the URL is not a data URL.
 *
 * Uses a lightweight inline parser to avoid data-urls (which pulls in webidl-conversions
 * that references SharedArrayBuffer, causing "SharedArrayBuffer is not defined" in browsers
 * without cross-origin isolation).
 */
function extractTextFromDataUrl(url: string): string | null {
  if (!url.startsWith('data:')) {
    return null;
  }
  const commaIndex = url.indexOf(',');
  if (commaIndex === -1) {
    return null;
  }
  const header = url.slice(5, commaIndex).toLowerCase();
  const body = url.slice(commaIndex + 1);

  let charset = 'utf-8';
  const charsetMatch = header.match(/charset=([^;]+)/);
  if (charsetMatch !== null) {
    charset = charsetMatch[1]!.trim();
  }

  if (header.endsWith(';base64')) {
    try {
      const binary = atob(body.replace(/-/g, '+').replace(/_/g, '/'));
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder(charset).decode(bytes);
    } catch {
      return null;
    }
  }

  try {
    return decodeURIComponent(body);
  } catch {
    return null;
  }
}

/**
 * Convert file parts with unsupported media types to inline text parts.
 *
 * The Vercel AI SDK providers (e.g. @ai-sdk/anthropic) only support a
 * limited set of file media types (images, PDFs). Text-based files like
 * CSV, JSON, etc. are rejected with "media type: X functionality not
 * supported". This function converts those unsupported file parts to
 * text parts with the file content inlined.
 *
 * @see https://github.com/vercel/ai/issues/7113
 */
function convertFilePartsForLlm(parts: UIMessage['parts']): UIMessage['parts'] {
  let hasUnsupportedFileParts = false;
  for (const part of parts) {
    const partAny = part as Record<string, unknown>;
    if (
      partAny['type'] === 'file' &&
      typeof partAny['mediaType'] === 'string' &&
      !SUPPORTED_FILE_MEDIA_TYPES.has(partAny['mediaType'])
    ) {
      hasUnsupportedFileParts = true;
      break;
    }
  }

  if (!hasUnsupportedFileParts) {
    return parts;
  }

  return parts.map((part) => {
    const partAny = part as Record<string, unknown>;
    const mediaType = partAny['mediaType'];
    if (
      partAny['type'] !== 'file' ||
      typeof mediaType !== 'string' ||
      SUPPORTED_FILE_MEDIA_TYPES.has(mediaType)
    ) {
      return part;
    }

    const filename = partAny['filename'] as string | undefined;
    const label =
      filename !== undefined ? `File: ${filename}` : 'Uploaded file';

    // Binary uploads (video, audio, archives, …) are not readable as text.
    // Inlining their raw bytes yields garbage and can exceed the provider's
    // 10 MiB per-content-block limit, which rejects the whole request. Replace
    // them with a short note so the model knows the file was attached.
    if (!isTextLikeMediaType(mediaType)) {
      return {
        type: 'text' as const,
        text: `[${label} (${mediaType}) — attached but omitted from model input: not readable as text]`,
      };
    }

    const textContent = extractTextFromDataUrl(partAny['url'] as string);
    if (textContent === null) {
      return part;
    }

    // Cap the inlined content so a single oversized text file can't exceed the
    // provider's per-block limit.
    const truncated = textContent.length > MAX_INLINED_FILE_CHARS;
    const boundedContent = truncated
      ? textContent.slice(0, MAX_INLINED_FILE_CHARS)
      : textContent;
    const suffix = truncated
      ? `\n[... truncated: file exceeded ${MAX_INLINED_FILE_CHARS} characters]`
      : '';

    return {
      type: 'text' as const,
      text: `${label}\n\`\`\`\n${boundedContent}\n\`\`\`${suffix}`,
    };
  });
}

export type SerializedMessage = {
  conversationId: string;
  uiMessage: UIMessage;
  type: MessageType;
  createdAt?: string;
  updatedAt?: string;
  /** Omitted in legacy data; defaults to 'ready' when deserializing. */
  status?: MessageStatus;
};

/**
 * Branded types for polymorphic message handling.
 * These allow type narrowing through type guards.
 */
declare const __messageBrand: unique symbol;
type MessageBrand<B> = { readonly [__messageBrand]: B };

export type UserMessage = Message & MessageBrand<'user'>;
export type CompactionMessage = Message & MessageBrand<'compaction'>;

/** 'streaming' = incomplete (e.g. process crashed mid-stream); excluded from getModelMessages. */
export type MessageStatus = 'ready' | 'streaming';

const coteraPartToUiParts = (part: CoteraPart): UIMessage['parts'] => {
  switch (part.type) {
    case 'text':
      if (part.text === null) {
        return [];
      }
      return [{ type: 'text', text: part.text }];
    case 'reasoning':
      if (part.text === null) {
        return [];
      }
      return [{ type: 'reasoning', text: part.text }];
    case 'image':
      if (part.image === null) {
        return [];
      }
      return [
        {
          type: 'file',
          mediaType: part.image.mimeType ?? 'image/png',
          url: part.image.data ?? part.image.url ?? '',
        },
      ];
    case 'file':
      if (part.file === null) {
        return [];
      }
      return [
        {
          type: 'file',
          mediaType: part.file.mimeType ?? 'application/octet-stream',
          url: part.file.data ?? '',
          ...(part.file.filename !== undefined && part.file.filename !== null
            ? { filename: part.file.filename }
            : {}),
        },
      ];
    case 'tool-call':
      if (part.toolCall === null) {
        return [];
      }
      return [
        {
          type: `tool-${part.toolCall.toolName}` as const,
          toolCallId: part.toolCall.toolCallId,
          input: part.toolCall.args,
          state: 'input-available' as const,
        },
      ];
    case 'tool-result':
      if (part.toolResult === null) {
        return [];
      }
      return [
        {
          type: `tool-${part.toolResult.toolName}` as const,
          toolCallId: part.toolResult.toolCallId,
          input: {},
          output: part.toolResult.result,
          state: 'output-available' as const,
        },
      ];
    default:
      return [];
  }
};

/**
 * Model class for a message in a conversation.
 * Encapsulates message manipulation logic including tool call deduplication.
 */
export class Message {
  protected _uiMessage: UIMessage;
  protected _type: MessageType;
  protected _actions: ChatAction[];
  protected _hydratedParts: MessagePart[];
  protected _createdAt: Date;
  protected _updatedAt: Date;
  protected _status: MessageStatus;
  protected _llmUsage: AssistantMessageLlmUsage | null;
  /**
   * User (actor) who authored this message row (`chat_messages.actor_id`).
   * Undefined for messages not hydrated from a persisted row. Used to label
   * user turns with `From: <name>` in multi-participant (group) chats.
   */
  protected _senderActorId?: string;
  // Cache for getToolParts(), keyed on `this.parts` array identity. `parts`
  // returns a stable array until a mutation reassigns it (see
  // refreshHydratedParts), so identity is an exact invalidation token.
  private _toolPartsSource: MessagePart[] | null = null;
  private _toolPartsResult: ToolPart[] | null = null;

  constructor(
    readonly conversationId: string,
    uiMessage: UIMessage,
    type: MessageType = 'chat',
    actions: ChatAction[] = [],
    meta?: {
      createdAt?: Date;
      updatedAt?: Date;
      status?: MessageStatus;
      llmUsage?: AssistantMessageLlmUsage | null;
      actorId?: string;
    }
  ) {
    this._uiMessage = this.deduplicateToolCallsInMessage(uiMessage);
    this._type = type;
    this._actions = actions;
    this._hydratedParts = this.refreshHydratedParts();
    this._createdAt = meta?.createdAt ?? new Date();
    this._updatedAt = meta?.updatedAt ?? this._createdAt;
    this._status = meta?.status ?? 'ready';
    this._llmUsage = meta?.llmUsage ?? null;
    this._senderActorId = meta?.actorId;
  }

  /**
   * User (actor) who authored this message, when hydrated from a persisted
   * `chat_messages` row. Undefined otherwise.
   */
  get senderActorId(): string | undefined {
    return this._senderActorId;
  }

  /** 'streaming' = incomplete; excluded from getModelMessages. */
  get status(): MessageStatus {
    return this._status;
  }

  /**
   * When the user sends a newer chat message while this row was still marked streaming,
   * marks it terminal so history matches a completed row (hydrate-time self-heal).
   */
  markInterruptedAssistantTerminal(): void {
    if (this._status !== 'streaming') {
      return;
    }
    this._status = 'ready';
    this.touchUpdatedAt();
  }

  /**
   * Rebuild hydrated parts from current _uiMessage.parts.
   * Called at construction and after any mutation that changes parts.
   */
  protected refreshHydratedParts(): MessagePart[] {
    const rawParts = this._uiMessage.parts ?? [];
    return rawParts.map((p) => hydratePart(p, this._actions));
  }

  /**
   * Get the UI message.
   */
  get uiMessage(): UIMessage {
    return this._uiMessage;
  }

  /**
   * Get the message type.
   */
  get type(): MessageType {
    return this._type;
  }

  /**
   * Get the message ID.
   */
  get id(): string | undefined {
    return this._uiMessage.id;
  }

  /**
   * Get the message role.
   */
  get role(): UIMessage['role'] {
    return this._uiMessage.role;
  }

  /**
   * Get the message parts as hydrated part classes (TextPart, ToolPart, etc.).
   * Hydrated at construction time and refreshed when parts are mutated.
   * For raw parts use raw().parts.
   */
  get parts(): MessagePart[] {
    return this._hydratedParts;
  }

  /**
   * Get message creation timestamp.
   */
  get createdAt(): Date {
    return this._createdAt;
  }

  /**
   * Get message last-update timestamp.
   */
  get updatedAt(): Date {
    return this._updatedAt;
  }

  /** LLM token usage and result-cache metadata when this row came from step-chat. */
  getLlmUsage(): AssistantMessageLlmUsage | null {
    return this._llmUsage;
  }

  setLlmUsage(usage: AssistantMessageLlmUsage): void {
    this._llmUsage = usage;
    this.touchUpdatedAt();
  }

  /**
   * Update the message's last-modified timestamp.
   */
  private touchUpdatedAt(): void {
    this._updatedAt = new Date();
  }

  /**
   * Check if this is an assistant message.
   * Type guard that narrows to AssistantMessage.
   * Base implementation - overridden in AssistantMessageClass.
   */
  isAssistantMessage(): this is AssistantMessage {
    return this._uiMessage.role === 'assistant';
  }

  /**
   * Check if this is a system message.
   * Type guard that narrows to SystemMessage when implemented on {@link SystemMessageClass}.
   */
  isSystemMessage(): this is SystemMessage {
    return this._uiMessage.role === 'system';
  }

  /**
   * Check if this is a user message.
   * Type guard that narrows to UserMessage.
   */
  isUserMessage(): this is UserMessage {
    return this._uiMessage.role === 'user';
  }

  /**
   * Hydrated tool parts in this message (empty for non-assistant messages).
   */
  getToolParts(): ToolPart[] {
    const source = this.parts;
    let result = this._toolPartsResult;
    if (this._toolPartsSource !== source || result === null) {
      result = source.filter((p): p is ToolPart => p.isToolPart());
      this._toolPartsSource = source;
      this._toolPartsResult = result;
    }
    return result;
  }

  /**
   * True when any tool part is still awaiting invocation (input / approval states).
   * Used by the LLM input builder generic renderers.
   */
  hasPendingToolInvocationParts(): boolean {
    return this.getToolParts().some((part) => {
      const state = part.raw().state;
      return (
        part.needsExecution() ||
        state === 'input-streaming' ||
        state === 'approval-requested' ||
        state === 'approval-responded'
      );
    });
  }

  /**
   * True when any tool part has a finished tool result for the LLM prompt.
   */
  hasCompletedToolParts(): boolean {
    return this.getToolParts().some((part) => {
      const state = part.raw().state;
      return state === 'output-available' || state === 'output-error';
    });
  }

  /** First tool call id in this message, if any. */
  findToolCallId(): string | undefined {
    return this.getToolParts()[0]?.toolCallId;
  }

  /**
   * Check if this is a rich user message (contains editor-state parts).
   * Type guard that narrows to RichUserMessage.
   * Only RichUserMessageClass instances can be RichUserMessage.
   */
  isRichUserMessage(): this is RichUserMessage {
    return (
      this instanceof RichUserMessageClass &&
      this.isUserMessage() &&
      this.hasEditorStateParts()
    );
  }

  /**
   * Check if this message contains editor-state parts.
   * Used internally by type guards.
   */
  protected hasEditorStateParts(): boolean {
    const parts = this._uiMessage.parts ?? [];
    for (const part of parts) {
      const partAny = part as any;
      if (
        typeof partAny.type === 'string' &&
        partAny.type === 'editor-state' &&
        'editorState' in partAny
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if this message is hidden from the UI.
   */
  isHidden(): boolean {
    return this._type === 'hidden';
  }

  /**
   * Check if this is a compaction message.
   * Type guard that narrows to CompactionMessage.
   */
  isCompactionMessage(): this is CompactionMessage {
    return this._type === 'compaction';
  }

  /**
   * Check if this is a compaction message.
   * @deprecated Use isCompactionMessage() instead for type narrowing
   */
  isCompaction(): boolean {
    return this._type === 'compaction';
  }

  /**
   * Find a tool call part by toolCallId.
   */
  findToolCallPart(toolCallId: string): UIMessage['parts'][0] | undefined {
    const parts = this._uiMessage.parts ?? [];
    return parts.find(
      (part) =>
        typeof part.type === 'string' &&
        part.type.startsWith('tool-') &&
        'toolCallId' in part &&
        part.toolCallId === toolCallId
    );
  }

  /**
   * Find all tool call parts with a specific toolCallId.
   */
  findToolCallParts(toolCallId: string): UIMessage['parts'] {
    const parts = this._uiMessage.parts ?? [];
    return parts.filter(
      (part) =>
        typeof part.type === 'string' &&
        part.type.startsWith('tool-') &&
        'toolCallId' in part &&
        part.toolCallId === toolCallId
    );
  }

  /**
   * Find tool call part without output (the call itself).
   * Returns a ToolPart instance if found, undefined otherwise.
   */
  findToolCallPartWithoutOutput(toolCallId: string): ToolPart | undefined {
    const parts = this._uiMessage.parts ?? [];
    const part = parts.find(
      (part) =>
        typeof part.type === 'string' &&
        part.type.startsWith('tool-') &&
        'toolCallId' in part &&
        part.toolCallId === toolCallId &&
        !('output' in part)
    );

    if (!part) {
      return undefined;
    }

    const hydrated = hydratePart(part, this._actions);
    return hydrated.type === 'tool' ? (hydrated as ToolPart) : undefined;
  }

  /**
   * Find result parts (with output) for a specific toolCallId.
   */
  findResultParts(toolCallId: string): UIMessage['parts'] {
    const parts = this._uiMessage.parts ?? [];
    return parts.filter(
      (part) =>
        typeof part.type === 'string' &&
        part.type.startsWith('tool-') &&
        'toolCallId' in part &&
        part.toolCallId === toolCallId &&
        'output' in part
    );
  }

  /**
   * Find the first result part (with output) for a toolCallId as a hydrated ToolPart.
   * Use this when you need the tool name or input from an existing result part.
   */
  findResultToolPart(toolCallId: string): ToolPart | undefined {
    const part = this.parts.find(
      (p): p is ToolPart =>
        p.isToolPart() && p.toolCallId === toolCallId && 'output' in p.raw()
    );
    return part;
  }

  /**
   * Get all tool keys used in this message.
   */
  getToolKeys(cocoToolKeys: CocoToolKeys): ToolKey[] {
    const toolKeys = new Set<string>();
    const parts = this._uiMessage.parts ?? [];

    for (const part of parts) {
      if (typeof part.type === 'string' && part.type.startsWith('tool-')) {
        const toolName = part.type.replace('tool-', '');
        try {
          const toolKey = ToolKey.fromLlmName(toolName);
          toolKeys.add(toolKey.toString());

          const loadResourceKey =
            cocoToolKeys.loadResource ?? COCO_LOAD_RESOURCE_TOOL_KEY;
          const tk = toolKey.toString();
          if (
            (tk === loadResourceKey ||
              tk === 'coco/load-tool' ||
              tk === 'coco/load-agent') &&
            'input' in part
          ) {
            const input = part.input as Record<string, unknown> | undefined;
            if (input !== undefined) {
              const nestedToolKey =
                input['t'] === 'tool' && typeof input['toolKey'] === 'string'
                  ? input['toolKey']
                  : tk === 'coco/load-tool' &&
                    typeof input['toolKey'] === 'string'
                  ? input['toolKey']
                  : null;
              if (nestedToolKey !== null) {
                try {
                  toolKeys.add(ToolKey.fromString(nestedToolKey).toString());
                } catch {
                  // Ignore invalid tool keys
                }
              }
              const loadedResourceId =
                typeof input['resourceId'] === 'string'
                  ? input['resourceId'].trim()
                  : '';
              if (
                loadedResourceId.length > 0 &&
                (input['resourceType'] === 'agent' || input['t'] === 'agent')
              ) {
                toolKeys.add(agentIdToAgentToolKey(loadedResourceId));
              }
              if (
                loadedResourceId.length > 0 &&
                (input['resourceType'] === 'workflow' ||
                  input['t'] === 'workflow')
              ) {
                toolKeys.add(workflowIdToWorkflowToolKey(loadedResourceId));
              }
              if (
                tk === 'coco/load-agent' &&
                typeof input['agentId'] === 'string' &&
                input['agentId'].trim().length > 0
              ) {
                toolKeys.add(agentIdToAgentToolKey(input['agentId']));
              }
            }
          }
        } catch {
          // Ignore invalid tool names
        }
      }

      // Extract tool keys from editor-state parts (tool + agent @mentions)
      const partAny = part as { type?: string; editorState?: DocumentState };
      if (
        partAny.type === 'editor-state' &&
        partAny.editorState !== undefined &&
        partAny.editorState !== null
      ) {
        try {
          const editor = new EditorState(partAny.editorState);
          const mentions = editor.getMentions(BuildExplorer.empty());
          for (const mention of mentions) {
            if (mention.type === 'tool') {
              const toolKeyStr =
                mention.referenceId ?? mention.id ?? mention.label;
              if (typeof toolKeyStr === 'string' && toolKeyStr.length > 0) {
                try {
                  toolKeys.add(ToolKey.fromString(toolKeyStr).toString());
                } catch {
                  // Ignore invalid tool keys
                }
              }
            }
            if (mention.type === 'agent') {
              const agentId = mention.referenceId ?? mention.id;
              if (typeof agentId === 'string' && agentId.trim().length > 0) {
                toolKeys.add(agentIdToAgentToolKey(agentId.trim()));
              }
            }
          }
          for (const ref of collectWorkspaceResourceRefs(partAny.editorState)) {
            if (ref.resourceType === ResourceType.Agent) {
              toolKeys.add(agentIdToAgentToolKey(ref.resourceId));
            }
          }
        } catch {
          // Ignore invalid editor state
        }
      }
    }

    return Array.from(toolKeys).map((key) => ToolKey.fromString(key));
  }

  /**
   * Tool keys that were loaded dynamically by tool calls in this message
   * (e.g. ask-for-credentials deferred tool or coco/load-resource tool target).
   */
  getDynamicallyLoadedToolKeys(cocoToolKeys: CocoToolKeys): ToolKey[] {
    const toolKeys = new Set<string>();
    const loadResourceKey =
      cocoToolKeys.loadResource ?? COCO_LOAD_RESOURCE_TOOL_KEY;

    for (const part of this.parts) {
      if (!part.isToolPart()) {
        continue;
      }

      const toolKeyStr = part.toolKey().toString();
      if (toolKeyStr === COCO_ASK_FOR_CREDENTIALS_TOOL_KEY) {
        const next =
          typeof part.input['next'] === 'object' &&
          part.input['next'] !== null &&
          !Array.isArray(part.input['next'])
            ? (part.input['next'] as Record<string, unknown>)
            : null;
        const deferredToolKey =
          next !== null && typeof next['toolKey'] === 'string'
            ? next['toolKey']
            : null;
        if (deferredToolKey !== null && deferredToolKey.trim().length > 0) {
          try {
            toolKeys.add(ToolKey.fromString(deferredToolKey).toString());
          } catch {
            // Ignore invalid tool keys
          }
        }
      }

      if (toolKeyStr === loadResourceKey || toolKeyStr === 'coco/load-tool') {
        const nestedToolKey =
          part.input['t'] === 'tool' &&
          typeof part.input['toolKey'] === 'string'
            ? part.input['toolKey']
            : toolKeyStr === 'coco/load-tool' &&
              typeof part.input['toolKey'] === 'string'
            ? part.input['toolKey']
            : null;
        if (nestedToolKey !== null && nestedToolKey.trim().length > 0) {
          try {
            toolKeys.add(ToolKey.fromString(nestedToolKey).toString());
          } catch {
            // Ignore invalid tool keys
          }
        }
      }
    }

    return Array.from(toolKeys).map((key) => ToolKey.fromString(key));
  }

  /**
   * Add a part to this message.
   */
  addPart(part: UIMessage['parts'][0]): void {
    const parts = this._uiMessage.parts ?? [];
    this._uiMessage = {
      ...this._uiMessage,
      parts: [...parts, part],
    };
    // Re-deduplicate after adding
    this._uiMessage = this.deduplicateToolCallsInMessage(this._uiMessage);
    this._hydratedParts = this.refreshHydratedParts();
    this.touchUpdatedAt();
  }

  /**
   * Replace a tool call part.
   */
  replaceToolCallPart(
    toolCallId: string,
    replacement: UIMessage['parts'][0]
  ): boolean {
    const parts = this._uiMessage.parts ?? [];
    const index = parts.findIndex(
      (part) =>
        typeof part.type === 'string' &&
        part.type.startsWith('tool-') &&
        'toolCallId' in part &&
        part.toolCallId === toolCallId
    );

    if (index >= 0) {
      const updatedParts = [...parts];
      updatedParts[index] = replacement;
      this._uiMessage = {
        ...this._uiMessage,
        parts: updatedParts,
      };
      // Re-deduplicate after replacing
      this._uiMessage = this.deduplicateToolCallsInMessage(this._uiMessage);
      this._hydratedParts = this.refreshHydratedParts();
      this.touchUpdatedAt();
      return true;
    }

    return false;
  }

  /**
   * Remove tool call parts by toolCallId.
   */
  removeToolCallParts(toolCallId: string): boolean {
    const parts = this._uiMessage.parts ?? [];
    const filteredParts = parts.filter(
      (part) =>
        !(
          typeof part.type === 'string' &&
          part.type.startsWith('tool-') &&
          'toolCallId' in part &&
          part.toolCallId === toolCallId
        )
    );

    if (filteredParts.length !== parts.length) {
      this._uiMessage = {
        ...this._uiMessage,
        parts: filteredParts,
      };
      this._hydratedParts = this.refreshHydratedParts();
      this.touchUpdatedAt();
      return true;
    }

    return false;
  }

  /**
   * Update the UI message (replaces the entire message).
   */
  updateUIMessage(uiMessage: UIMessage): void {
    this._uiMessage = this.deduplicateToolCallsInMessage(uiMessage);
    this._hydratedParts = this.refreshHydratedParts();
    this.touchUpdatedAt();
  }

  /**
   * Get the raw UIMessage for UI usage.
   * This exposes the underlying storage format for cases where it's needed (e.g., UI rendering).
   */
  raw(): UIMessage {
    return this._uiMessage;
  }

  toText(): string {
    return this.parts
      .map((part) => part.toText())
      .filter((text) => text !== '')
      .join('\n');
  }

  /**
   * Deterministic content signature for equality: text body and tool call input/output.
   * Used by Conversation.equals() to avoid treating messages as equal when content changed.
   */
  contentSignature(): string {
    const parts = this._uiMessage.parts ?? [];
    const segments: string[] = [];
    for (const part of parts) {
      const p = part as Record<string, unknown>;
      if (p['type'] === 'text' && typeof p['text'] === 'string') {
        segments.push(`t:${p['text']}`);
      } else if (
        typeof p['type'] === 'string' &&
        (p['type'] as string).startsWith('tool-') &&
        'toolCallId' in p
      ) {
        const toolCallId = String(p['toolCallId'] ?? '');
        const state = String(p['state'] ?? '');
        const input =
          p['input'] !== undefined ? JSON.stringify(p['input']) : '';
        const output =
          p['output'] !== undefined ? JSON.stringify(p['output']) : '';
        segments.push(`c:${toolCallId}:${state}:${input}:${output}`);
      } else if (
        typeof p['type'] === 'string' &&
        (p['type'] as string) === 'dynamic-tool' &&
        'toolCallId' in p
      ) {
        const toolCallId = String(p['toolCallId'] ?? '');
        const state = String(p['state'] ?? '');
        const input =
          p['input'] !== undefined ? JSON.stringify(p['input']) : '';
        const output =
          p['output'] !== undefined ? JSON.stringify(p['output']) : '';
        segments.push(`d:${toolCallId}:${state}:${input}:${output}`);
      } else {
        segments.push(`u:${JSON.stringify(part)}`);
      }
    }
    return JSON.stringify(segments);
  }

  /**
   * Stable cache identity for this message (excludes transient UI ids).
   * Used by {@link LLMInputBuilder.build} cache keys and LLM response caches.
   */
  stableCacheKey(): string {
    const { id: _id, ...uiWithoutId } = this.raw();
    return JSON.stringify({
      type: this._type,
      ui: uiWithoutId,
    });
  }

  /**
   * Serialize this message to a plain object.
   */
  serialize(): SerializedMessage {
    return {
      conversationId: this.conversationId,
      uiMessage: this._uiMessage,
      type: this._type,
      createdAt: this._createdAt.toISOString(),
      updatedAt: this._updatedAt.toISOString(),
      status: this._status,
    };
  }

  /**
   * Create a Message from serialized data.
   * Uses the factory method to instantiate the correct subclass.
   */
  static fromSerialized(
    serialized: SerializedMessage,
    actions: ChatAction[] = []
  ): Message {
    return Message.fromUIMessage(
      serialized.conversationId,
      serialized.uiMessage,
      serialized.type,
      actions,
      {
        createdAt:
          serialized.createdAt !== undefined
            ? new Date(serialized.createdAt)
            : undefined,
        updatedAt:
          serialized.updatedAt !== undefined
            ? new Date(serialized.updatedAt)
            : undefined,
        status: serialized.status === 'streaming' ? 'streaming' : 'ready',
      }
    );
  }

  /**
   * Create a clone of this message.
   */
  clone(): Message {
    return Message.fromUIMessage(
      this.conversationId,
      { ...this._uiMessage, parts: [...(this._uiMessage.parts ?? [])] },
      this._type,
      this._actions,
      {
        createdAt: this._createdAt,
        updatedAt: this._updatedAt,
        status: this._status,
      }
    );
  }

  /**
   * Convert this message to a UIMessage suitable for LLM consumption.
   * Base implementation converts file parts for the model; subclasses extend for
   * editor-state / workspace resolution (see {@link RichUserMessageClass#toLlmAsync}).
   *
   * @param opts.graph - Optional {@link BuildExplorer} for data-graph mention resolution
   *   in rich user messages; ignored by the base implementation.
   */
  async toLlmAsync(_opts?: {
    workspaceDomainLoader?: WorkspaceDomainLoader;
    graph?: BuildExplorer;
  }): Promise<UIMessage> {
    const parts = this._uiMessage.parts ?? [];
    const convertedParts = convertFilePartsForLlm(parts);
    if (convertedParts === parts) {
      return this._uiMessage;
    }
    return {
      ...this._uiMessage,
      parts: convertedParts,
    };
  }

  /**
   * Check if a UIMessage contains editor-state parts.
   * Used by the factory method to determine which class to instantiate.
   */
  protected static hasEditorStateParts(uiMessage: UIMessage): boolean {
    if (uiMessage.role !== 'user' && uiMessage.role !== 'system') {
      return false;
    }

    const parts = uiMessage.parts ?? [];
    for (const part of parts) {
      const partAny = part as any;
      if (
        typeof partAny.type === 'string' &&
        partAny.type === 'editor-state' &&
        'editorState' in partAny
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Factory method to create the appropriate Message subclass based on message content.
   * At hydration time, this determines whether to instantiate Message, AssistantMessageClass, or RichUserMessageClass.
   */
  static fromUIMessage(
    conversationId: string,
    uiMessage: UIMessage,
    type: MessageType = 'chat',
    actions: ChatAction[] = [],
    meta?: {
      createdAt?: Date;
      updatedAt?: Date;
      status?: MessageStatus;
      llmUsage?: AssistantMessageLlmUsage | null;
      actorId?: string;
    }
  ): Message {
    if (uiMessage.role === 'assistant') {
      return new AssistantMessageClass(
        conversationId,
        uiMessage,
        type,
        actions,
        meta
      );
    }
    if (uiMessage.role === 'system') {
      return new SystemMessageClass(
        conversationId,
        uiMessage,
        type,
        actions,
        meta
      );
    }
    if (uiMessage.role === 'user') {
      if (this.hasEditorStateParts(uiMessage)) {
        return new RichUserMessageClass(
          conversationId,
          uiMessage,
          type,
          actions,
          meta
        );
      }
      return new UserMessageClass(
        conversationId,
        uiMessage,
        type,
        actions,
        meta
      );
    }
    return new Message(conversationId, uiMessage, type, actions, meta);
  }

  /**
   * Hydrate a {@link CoteraAgentMessage} (ATC KV / llm v1 pipeline) into the appropriate
   * {@link Message} subclass for this conversation.
   */
  static fromV1(
    conversationId: string,
    coteraMessage: CoteraAgentMessage,
    type: MessageType = 'chat'
  ): Message {
    const parts = coteraMessage.content.flatMap(coteraPartToUiParts);
    const role =
      coteraMessage.role === 'tool'
        ? 'assistant'
        : (coteraMessage.role as 'user' | 'assistant' | 'system');
    const uiMessage: UIMessage = {
      id: coteraMessage.id ?? v4(),
      role,
      parts,
    };
    return Message.fromUIMessage(conversationId, uiMessage, type);
  }

  /**
   * Deduplicate tool call parts within a message.
   * Removes duplicate tool call parts (same toolCallId) and duplicate result parts.
   * Prefers actual tool results (tool-*) over dynamic-tool errors.
   */
  private deduplicateToolCallsInMessage(uiMessage: UIMessage): UIMessage {
    const parts = uiMessage.parts ?? [];
    if (parts.length === 0) {
      return uiMessage;
    }

    // Track seen tool call IDs and their parts to handle duplicates
    const toolCallMap = new Map<string, (typeof parts)[0]>();
    const resultMap = new Map<string, (typeof parts)[0]>();
    const otherParts: typeof parts = [];

    // First pass: collect all tool parts by toolCallId
    for (const part of parts) {
      const isToolPart =
        typeof part.type === 'string' &&
        (part.type.startsWith('tool-') || part.type === 'dynamic-tool') &&
        'toolCallId' in part;

      if (isToolPart) {
        const toolCallId = part.toolCallId;
        const hasOutput = 'output' in part;
        const isDynamicTool = part.type === 'dynamic-tool';

        if (hasOutput) {
          // This is a result part
          const existing = resultMap.get(toolCallId);
          // Prefer non-dynamic-tool results over dynamic-tool
          if (
            !existing ||
            (existing.type === 'dynamic-tool' && !isDynamicTool)
          ) {
            resultMap.set(toolCallId, part);
          }
        } else {
          // This is a tool call part (not a result)
          const existing = toolCallMap.get(toolCallId);
          // Prefer non-dynamic-tool calls over dynamic-tool
          if (
            !existing ||
            (existing.type === 'dynamic-tool' && !isDynamicTool)
          ) {
            toolCallMap.set(toolCallId, part);
          }
        }
      } else {
        // Not a tool part, keep it as-is
        otherParts.push(part);
      }
    }

    // Reconstruct parts array: non-tool parts, then tool calls, then results
    // This maintains a reasonable ordering
    const deduplicatedParts: typeof parts = [
      ...otherParts,
      ...Array.from(toolCallMap.values()),
      ...Array.from(resultMap.values()),
    ];

    // Only create new message if parts changed
    if (deduplicatedParts.length === parts.length) {
      return uiMessage;
    }

    return {
      ...uiMessage,
      parts: deduplicatedParts,
    };
  }
}

/**
 * Joins text parts from a UIMessage into a single streamText `system` string.
 */
export function flattenUiMessageToSystemString(uiMessage: UIMessage): string {
  const parts = uiMessage.parts ?? [];
  const chunks: string[] = [];
  for (const part of parts) {
    if (
      typeof part === 'object' &&
      part !== null &&
      'type' in part &&
      part.type === 'text' &&
      'text' in part &&
      typeof part.text === 'string' &&
      part.text.length > 0
    ) {
      chunks.push(part.text);
    }
  }
  return chunks.join('\n\n').trim();
}

/**
 * Resolves a {@link Message} to the streamText system string (editor-state → markdown).
 */
export async function resolveMessageToSystemString(
  message: Message,
  opts?: {
    workspaceDomainLoader?: WorkspaceDomainLoader;
    graph?: BuildExplorer;
  }
): Promise<string> {
  if (message instanceof SystemMessageClass) {
    return message.resolveToSystemString(opts);
  }
  const ui = await message.toLlmAsync(opts);
  return flattenUiMessageToSystemString(ui);
}

/**
 * Plain user message (text parts only). Use {@link RichUserMessageClass} for editor-state.
 */
export class UserMessageClass extends Message {
  constructor(
    conversationId: string,
    uiMessage: UIMessage,
    type: MessageType = 'chat',
    actions: ChatAction[] = [],
    meta?: {
      createdAt?: Date;
      updatedAt?: Date;
      status?: MessageStatus;
    }
  ) {
    super(conversationId, uiMessage, type, actions, meta);
  }

  /** Warehouse / scheduled agent runs: single text user turn to start the job. */
  static fromString(conversationId: string, text: string): UserMessageClass {
    return new UserMessageClass(conversationId, {
      id: v4(),
      role: 'user',
      parts: [{ type: 'text', text }],
    });
  }

  override isUserMessage(): this is UserMessage {
    return this.uiMessage.role === 'user';
  }
}

/**
 * System message class: persisted coco/ad-hoc payloads and linked-agent editor-state.
 */
export class SystemMessageClass extends Message {
  static coco(conversationId: string): SystemMessageClass {
    return SystemMessageClass.fromStoredPayload(conversationId, { t: 'coco' });
  }

  static adHoc(conversationId: string, message: string): SystemMessageClass {
    return SystemMessageClass.fromStoredPayload(conversationId, {
      t: 'ad-hoc',
      message,
    });
  }

  static fromStoredPayload(
    conversationId: string,
    payload: StoredSystemMessagePayload
  ): SystemMessageClass {
    return Message.fromUIMessage(
      conversationId,
      {
        id: CONVERSATION_SYSTEM_MESSAGE_ID,
        role: 'system',
        parts: [storedSystemMessageUiPart(payload)],
      },
      'hidden'
    ) as SystemMessageClass;
  }

  /**
   * Ephemeral system for agent-linked chats (not persisted). Editor-state is
   * expanded in {@link resolveToSystemString} / {@link toLlmAsync}.
   */
  static forLinkedAgent(
    conversationId: string,
    agent: Agent,
    opts?: { parentId?: string | null }
  ): SystemMessageClass {
    const parts: NonNullable<UIMessage['parts']> = [
      {
        type: 'editor-state',
        editorState: agent.editorState.getState(),
      } as unknown as UIMessage['parts'][number],
    ];

    const parentId = opts?.parentId;
    if (parentId !== undefined && parentId !== null && parentId !== '') {
      const guardText = coco.appendSubAgentSelfInvocationGuard('', agent.id);
      if (guardText.trim().length > 0) {
        parts.push({ type: 'text', text: guardText });
      }
    }

    return new SystemMessageClass(conversationId, {
      id: `system-linked-agent-${agent.id}`,
      role: 'system',
      parts,
    });
  }

  /** Persisted system row (coco / ad-hoc), not compaction summaries or linked-agent synthesis. */
  static isConversationSystemStorage(message: Message): boolean {
    if (!(message instanceof SystemMessageClass)) {
      return false;
    }
    return message.getStoredPayload() !== null;
  }

  override isSystemMessage(): this is SystemMessage {
    return true;
  }

  getStoredPayload(): StoredSystemMessagePayload | null {
    if (this.uiMessage.id !== CONVERSATION_SYSTEM_MESSAGE_ID) {
      return null;
    }
    return extractStoredPayloadFromParts(this.uiMessage.parts ?? []);
  }

  /** Shape written to `chat_messages.message` (parts only, role lives on the row). */
  persistedMessageBody(): { parts: StoredSystemMessageUIPart[] } {
    const payload = this.getStoredPayload();
    if (payload === null) {
      throw new Error(
        'SystemMessageClass.persistedMessageBody requires a stored coco/ad-hoc payload'
      );
    }
    return persistedSystemMessageBody(payload);
  }

  /**
   * Uses stored coco/ad-hoc payload (not expanded markdown) so caches stay stable
   * when workspace mentions resolve differently at render time.
   */
  override stableCacheKey(): string {
    const payload = this.getStoredPayload();
    if (payload !== null) {
      return JSON.stringify({
        type: this._type,
        ...persistedSystemMessageBody(payload),
      });
    }
    return super.stableCacheKey();
  }

  async resolveToSystemString(opts?: {
    workspaceDomainLoader?: WorkspaceDomainLoader;
    graph?: BuildExplorer;
  }): Promise<string> {
    const payload = this.getStoredPayload();
    if (payload?.t === 'coco') {
      return coco.SYSTEM_MESSAGE;
    }
    if (payload?.t === 'ad-hoc') {
      return payload.message;
    }
    const ui = await this.toLlmAsync(opts);
    return flattenUiMessageToSystemString(ui);
  }

  override async toLlmAsync(opts?: {
    workspaceDomainLoader?: WorkspaceDomainLoader;
    graph?: BuildExplorer;
  }): Promise<UIMessage> {
    const payload = this.getStoredPayload();
    if (payload?.t === 'coco') {
      return {
        ...this.uiMessage,
        parts: [{ type: 'text', text: coco.SYSTEM_MESSAGE }],
      };
    }
    if (payload?.t === 'ad-hoc') {
      return {
        ...this.uiMessage,
        parts: [{ type: 'text', text: payload.message }],
      };
    }

    const base = await super.toLlmAsync({ graph: opts?.graph });
    const parts = base.parts ?? [];
    const transformedParts: UIMessage['parts'] = [];

    for (const part of parts) {
      const partAny = part as {
        type?: string;
        editorState?: unknown;
        text?: string;
      };
      if (
        typeof partAny.type === 'string' &&
        partAny.type === 'editor-state' &&
        partAny.editorState !== undefined &&
        partAny.editorState !== null
      ) {
        const editorState = partAny.editorState as DocumentState;
        let markdown: string;
        if (opts?.graph !== undefined) {
          const editor = new EditorState(editorState);
          markdown = editor.toPromptMarkdown(opts.graph, 'simple');
        } else if (opts?.workspaceDomainLoader !== undefined) {
          const editor = new WorkspacePromptEditor(
            editorState,
            opts.workspaceDomainLoader
          );
          markdown = await editor.toMarkdownPrompt();
        } else {
          const editor = new WorkspacePromptEditor(
            editorState,
            noopWorkspaceDomainLoader
          );
          markdown = editor.toPromptMarkdown(
            emptyWorkspaceRenderBundle,
            'simple'
          );
        }

        transformedParts.push({
          type: 'text',
          text: markdown,
        });
      } else {
        transformedParts.push(part);
      }
    }

    return {
      ...base,
      parts: transformedParts,
    };
  }

  /**
   * Tool and agent tool keys from @mentions in editor-state system parts.
   * Used so explicit mentions override {@link Conversation.getExcludedToolKeys}.
   */
  async getMentionedToolKeysAsync(opts?: {
    workspaceDomainLoader?: WorkspaceDomainLoader;
  }): Promise<string[]> {
    const keys = new Set<string>();
    const loader = opts?.workspaceDomainLoader ?? noopWorkspaceDomainLoader;
    const useRecursive = opts?.workspaceDomainLoader !== undefined;

    for (const part of this.uiMessage.parts ?? []) {
      const partAny = part as { type?: string; editorState?: unknown };
      if (
        partAny.type !== 'editor-state' ||
        partAny.editorState === undefined ||
        partAny.editorState === null
      ) {
        continue;
      }
      const editor = new WorkspacePromptEditor(
        partAny.editorState as DocumentState,
        loader
      );
      const mentioned = useRecursive
        ? await editor.toolsRecursive()
        : editor.tools();
      for (const key of mentioned) {
        keys.add(key);
      }
    }

    return [...keys];
  }
}

/**
 * Rich user message class that extends Message.
 * Overrides {@link Message#toLlmAsync} to convert editor-state parts to markdown.
 */
export class RichUserMessageClass extends Message {
  /**
   * Check if this is a rich user message (contains editor-state parts).
   * Type guard that narrows to RichUserMessage.
   * Overrides base class method for more specific type narrowing.
   */
  override isRichUserMessage(): this is RichUserMessage {
    return this.isUserMessage() && this.hasEditorStateParts();
  }

  /**
   * File parts are converted via {@link Message#toLlmAsync}, then `editor-state` parts
   * become markdown: {@link EditorState} + `graph` when `opts.graph` is set, else
   * {@link WorkspacePromptEditor} with `workspaceDomainLoader` or the noop empty bundle.
   */
  override async toLlmAsync(opts?: {
    workspaceDomainLoader?: WorkspaceDomainLoader;
    graph?: BuildExplorer;
  }): Promise<UIMessage> {
    const base = await super.toLlmAsync({ graph: opts?.graph });
    const parts = base.parts ?? [];
    const transformedParts: UIMessage['parts'] = [];

    for (const part of parts) {
      const partAny = part as any;
      if (
        typeof partAny.type === 'string' &&
        partAny.type === 'editor-state' &&
        'editorState' in partAny
      ) {
        const editorState = partAny.editorState as DocumentState;
        let markdown: string;
        if (opts?.graph !== undefined) {
          const editor = new EditorState(editorState);
          markdown = editor.toPromptMarkdown(opts.graph, 'simple');
        } else if (opts?.workspaceDomainLoader !== undefined) {
          const editor = new WorkspacePromptEditor(
            editorState,
            opts.workspaceDomainLoader
          );
          markdown = await editor.toMarkdownPrompt();
        } else {
          const editor = new WorkspacePromptEditor(
            editorState,
            noopWorkspaceDomainLoader
          );
          markdown = editor.toPromptMarkdown(
            emptyWorkspaceRenderBundle,
            'simple'
          );
        }

        transformedParts.push({
          type: 'text',
          text: markdown,
        });
      } else {
        transformedParts.push(part);
      }
    }

    return {
      ...base,
      parts: transformedParts,
    };
  }
}

/**
 * Assistant message class that extends Message.
 * Provides specialized methods for assistant messages.
 */
export class AssistantMessageClass extends Message {
  constructor(
    conversationId: string,
    uiMessage: UIMessage,
    type: MessageType = 'chat',
    actions: ChatAction[] = [],
    meta?: {
      createdAt?: Date;
      updatedAt?: Date;
      status?: MessageStatus;
    }
  ) {
    super(conversationId, uiMessage, type, actions, meta);
  }

  // Cache keyed on `super.parts` (the base `_hydratedParts`) array identity.
  // MUST NOT be hoisted into the constructor: `_hydratedParts` is reassigned to
  // a fresh array by every mutation (addPart / replaceToolCallPart /
  // removeToolCallParts / updateUIMessage), and array identity is exactly what
  // invalidates this cache.
  private _dedupedPartsSource: MessagePart[] | null = null;
  private _dedupedPartsResult: MessagePart[] | null = null;

  override get parts(): MessagePart[] {
    const source = super.parts;
    let result = this._dedupedPartsResult;
    if (this._dedupedPartsSource !== source || result === null) {
      result = deduplicateAskForCredentials(source);
      this._dedupedPartsSource = source;
      this._dedupedPartsResult = result;
    }
    return result;
  }

  /**
   * Check if this is an assistant message.
   * Type guard that narrows to AssistantMessage.
   * Overrides base class method for more specific type narrowing.
   */
  override isAssistantMessage(): this is AssistantMessage {
    return this.uiMessage.role === 'assistant';
  }

  hasIncompleteToolCalls(): boolean {
    return this.getToolParts().some((p) => p.isIncomplete());
  }

  getCredentialRequestParts(): ToolPart[] {
    return [...this.parts.filter(isCredentialRequestToolPart)].sort((a, b) => {
      // Completed (false) comes before incomplete (true)
      return Number(a.isIncomplete()) - Number(b.isIncomplete());
    });
  }

  getTextOutput(): string | null {
    const textParts = this.parts.map((p) => (p.isTextPart() ? p.text : null));
    if (textParts.length === 0) {
      return null;
    }
    return textParts.join('\n');
  }

  getOutput(): JsonValue | string | null {
    const parts = this.raw().parts ?? [];
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i] as
        | { type?: string; data?: unknown }
        | { type?: string };
      if (
        typeof part.type === 'string' &&
        part.type.startsWith('data-') &&
        'data' in part
      ) {
        return part.data as JsonValue;
      }
    }

    const textOutput = this.getTextOutput();
    if (textOutput === null || textOutput.trim() === '') {
      return null;
    }

    try {
      const parsed = JSON.parse(textOutput) as unknown;
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as JsonValue;
      }
    } catch {
      // Not JSON text; return plain assistant text output.
    }

    return textOutput;
  }
}

/**
 * Branded type for assistant messages.
 * Defined after AssistantMessageClass to allow proper type reference.
 */
export type AssistantMessage = AssistantMessageClass &
  MessageBrand<'assistant'>;

/**
 * Branded type for rich user messages (messages with editor-state parts).
 * Defined after RichUserMessageClass to allow proper type reference.
 */
export type RichUserMessage = RichUserMessageClass & MessageBrand<'rich-user'>;

export type SystemMessage = SystemMessageClass & MessageBrand<'system'>;

/**
 * Build a deduplication key for a credential request tool part.
 * Uses output credentialInfo if available, otherwise falls back to input.
 */
function getCredentialRequestKey(p: ToolPart): string {
  const output = p.getOutput<{
    credentialInfo?: {
      provider: string;
      strategies: { strategy: unknown }[];
    };
  }>();

  const credentialInfo =
    output?.credentialInfo ??
    (
      p.input as {
        credentialInfo?: {
          provider: string;
          strategies: { strategy: unknown }[];
        };
      }
    )?.credentialInfo;

  if (credentialInfo !== undefined) {
    const strategy = credentialInfo.strategies
      .map((s: { strategy: unknown }) => JSON.stringify(s.strategy))
      .join(',');
    return `${credentialInfo.provider}:${strategy}`;
  }

  // Fallback: use toolKey + toolCallId for parts without credentialInfo
  return `${p.toolKey().toString()}:${p.toolCallId}`;
}

const deduplicateAskForCredentials = (parts: MessagePart[]): MessagePart[] => {
  // Track seen credential request keys and their positions in the result
  const seenCredentialKeys = new Map<string, number>(); // key -> index in result
  const result: MessagePart[] = [];

  for (const p of parts) {
    if (!isCredentialRequestToolPart(p)) {
      // Non-credential parts: keep as-is
      result.push(p);
      continue;
    }

    const key = getCredentialRequestKey(p);
    const existingIndex = seenCredentialKeys.get(key);

    if (existingIndex === undefined) {
      // First time seeing this key: add to result
      seenCredentialKeys.set(key, result.length);
      result.push(p);
    } else if (!p.isIncomplete()) {
      // We've seen this key before, and this one is complete
      // Replace the existing one (which should be incomplete based on sort order in getCredentialRequestParts)
      const existingPart = result[existingIndex];
      if (existingPart?.isToolPart() && existingPart.isIncomplete()) {
        result[existingIndex] = p;
      }
      // If existing is already complete, drop this duplicate
    }
    // If this is incomplete and we've seen the key before, drop it
  }

  return result;
};
